import { AlertTriangle, Check, Loader2, Play, X } from "lucide-react";
import { Button } from "@/components/atoms/studio/ui/button";

/**
 * Approve / reject controls for a gated action, plus the banner shown when the approval call itself
 * fails.
 *
 * These lived in the Testing module and were reached across a module boundary by the migrations
 * engine gate. Testing no longer has an approval step — the backend removed it — so migrations, the
 * only remaining consumer, owns them now.
 *
 *  - "Approve & Run"  → approve, then run, as one explicit action (primary)
 *  - "Approve"        → approve only, leaving the run for later
 *  - "Reject"         → decline (optional; omitted where there is no reject handler)
 */
export function ApprovalActions({
	approving,
	onApprove,
	onApproveAndRun,
	onReject,
	compact,
	disabled,
}: {
	approving?: boolean;
	onApprove?: () => void;
	onApproveAndRun?: () => void;
	onReject?: () => void;
	/** Chat-card sizing (h-7/text-xs) vs the denser run-card row (h-6/text-[11px]). */
	compact?: boolean;
	disabled?: boolean;
}) {
	const h = compact ? "h-6 px-2 text-[11px]" : "h-7 text-xs";
	const icon = compact ? "h-3 w-3" : "h-3.5 w-3.5";
	const busy = Boolean(approving);
	return (
		<>
			{onApproveAndRun && (
				<Button
					size="sm"
					className={h}
					onClick={onApproveAndRun}
					disabled={busy || disabled}
					title="Approve this plan and run it now"
				>
					{busy ? (
						<Loader2 className={`mr-1 ${icon} animate-spin`} />
					) : (
						<Play className={`mr-1 ${icon} fill-current`} />
					)}
					{busy ? "Approving…" : "Approve & Run"}
				</Button>
			)}
			{onApprove && (
				<Button
					size="sm"
					variant="outline"
					className={h}
					onClick={onApprove}
					disabled={busy || disabled}
					title="Approve this plan without running it yet"
				>
					<Check className={`mr-1 ${icon}`} />
					Approve
				</Button>
			)}
			{onReject && (
				<Button
					size="sm"
					variant="ghost"
					className={`${h} text-muted-foreground hover:text-destructive-strong`}
					onClick={onReject}
					disabled={busy || disabled}
					title="Reject this plan"
				>
					<X className={`mr-1 ${icon}`} />
					Reject
				</Button>
			)}
		</>
	);
}

/** Approval itself failed (validation blocker, permissions, network). Dismissible, retryable. */
export function ApproveErrorBanner({
	message,
	onDismiss,
	onRetry,
	approving,
}: {
	message: string;
	onDismiss?: () => void;
	onRetry?: () => void;
	approving?: boolean;
}) {
	return (
		<div className="flex flex-wrap items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive-strong">
			<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
			<span className="min-w-0 flex-1 break-words leading-relaxed">
				<span className="font-semibold">Could not approve: </span>
				{message}
			</span>
			<span className="flex shrink-0 items-center gap-1.5">
				{onRetry && (
					<Button
						size="sm"
						variant="outline"
						className="h-6 px-2 text-[11px]"
						onClick={onRetry}
						disabled={approving}
					>
						{approving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
						Try again
					</Button>
				)}
				{onDismiss && (
					<Button
						size="sm"
						variant="ghost"
						className="h-6 px-2 text-[11px]"
						onClick={onDismiss}
					>
						Dismiss
					</Button>
				)}
			</span>
		</div>
	);
}
